import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { H1 } from "simplydo/react/ui/header";

import { useAppSelector } from "store";

import { MultiLineParagraph } from "components/lib/UI";

const COLLAPSED_HEIGHT = 52;

const WelcomeWrapper = styled.div`
  max-width: 720px;
  color: rgba(0, 0, 0, 0.6);
`;

const WelcomeClip = styled.div<{
  $expanded: boolean;
  $fullHeight: number;
  $needsClamp: boolean;
}>`
  position: relative;
  overflow: hidden;
  max-height: ${({ $expanded, $fullHeight, $needsClamp }) => {
    if (!$needsClamp || $expanded) {
      return $fullHeight > 0 ? `${$fullHeight}px` : "none";
    }
    return `${COLLAPSED_HEIGHT}px`;
  }};
  transition: max-height 0.35s ease;

  -webkit-mask-image: none;
  mask-image: none;

  &.faded {
    -webkit-mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 45%,
      transparent 100%
    );
    mask-image: linear-gradient(to bottom, #000 0%, #000 45%, transparent 100%);
  }
`;

const WelcomeContent = styled.div`
  p {
    margin: 0;
  }
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.primary.solid};
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9em;

  &:hover {
    text-decoration: underline;
  }
`;

const HeaderBlock = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);
  const dashboardText =
    user?.ownerOrganisation?.dashboardText || t("home.welcome.info");
  const [expanded, setExpanded] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const [needsClamp, setNeedsClamp] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!dashboardText || !element) {
      setFullHeight(0);
      setNeedsClamp(false);
      return;
    }

    const measure = () => {
      const height = element.scrollHeight;
      setFullHeight(height);
      setNeedsClamp(height > COLLAPSED_HEIGHT + 1);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [dashboardText, theme.sizes.isComputer]);

  const firstName = user?.profile?.firstName;
  const showFade = needsClamp && !expanded;

  return (
    <div style={{ gridColumn: theme.sizes.isComputer ? "span 2" : "span 1" }}>
      <H1 style={{ margin: 0 }}>
        {firstName ? `${firstName}'s dashboard` : "Your dashboard"}
      </H1>
      <WelcomeWrapper>
        <WelcomeClip
          $expanded={expanded}
          $fullHeight={fullHeight}
          $needsClamp={needsClamp}
          className={cx(showFade && "faded")}
        >
          <WelcomeContent ref={contentRef}>
            <MultiLineParagraph content={dashboardText} />
          </WelcomeContent>
        </WelcomeClip>
        {needsClamp ? (
          <ReadMoreButton
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Read more"}
          </ReadMoreButton>
        ) : null}
      </WelcomeWrapper>
    </div>
  );
};

export default HeaderBlock;
